import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { RaterListDto, RaterListItemDto } from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import { createRater, fetchRaters } from '../../services/dashboard.service.ts';
import { useCan } from '../../stores/auth.store.ts';
import { InvitationRun } from './InvitationRun.tsx';

/**
 * Everyone asked to assess somebody.
 *
 * The evaluated people have had a screen since the beginning; the raters are
 * three quarters of everybody involved and had none. They are also where the
 * operational work is — a wrong address, an invitation that never went out,
 * somebody two thirds of the way through — and none of that was visible.
 *
 * Progress is shown in people covered rather than as "submitted / not
 * submitted". A rater assessing nine people submits once, at the very end;
 * until then they look identical to somebody who never started, and the
 * difference decides who is worth a phone call.
 */
/**
 * How much of their questionnaire is filled in, as a bar and nothing else.
 *
 * It tracks answers rather than people finished: the multiple format asks one
 * behaviour across everybody before moving on, so a rater is done with nobody
 * until near the very end, and a bar stuck at zero for two thirds of the work
 * is the same picture as never having started.
 *
 * Only the share is reported, never people finished. In this format that
 * count sits at zero for the whole questionnaire and then jumps to all of
 * them at the end — it describes the last click rather than the work, so it
 * says nothing on any of the days somebody might be chased. The share is
 * carried as the bar's accessible name and its tooltip, so a bar with no text
 * beside it is still readable exactly.
 */
const Progress = ({ share: rate }: { share: number }) => {
  const { t } = useI18n();
  const share = Math.min(Math.max(rate, 0), 100);
  const label = t('raters.progressLabel', { percent: share });

  return (
    <span
      role="progressbar"
      aria-valuenow={share}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      title={label}
      className="block h-1.5 w-24 overflow-hidden rounded-full bg-line-soft"
    >
      <span
        className="block h-full rounded-full bg-teal transition-[width] duration-500"
        style={{ width: `${share}%` }}
      />
    </span>
  );
};

/**
 * Adding somebody who was not on the client's map.
 *
 * Four fields and no list: who they assess is decided on their own screen,
 * usually in a later conversation than the one that produced the name. The
 * same shape as the coach and P&O forms — one row of fields on a wide screen,
 * wrapping on a narrow one — because it is the same act.
 */
const NewRater = ({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (list: RaterListDto) => void;
}) => {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ fullName: '', email: '', jobTitle: '', area: '' });

  const complete = form.fullName.trim().length > 1 && form.email.trim().length > 0;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await createRater({
          fullName: form.fullName,
          email: form.email,
          jobTitle: form.jobTitle || null,
          area: form.area || null,
        }),
      );
      setForm({ fullName: '', email: '', jobTitle: '', area: '' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const field = (key: keyof typeof form, type = 'text') => (
    <label className="block min-w-[150px] flex-1">
      <span className="block font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
        {t(`raters.field.${key}`)}
      </span>
      <input
        type={type}
        value={form[key]}
        autoComplete="off"
        // An example of the shape the client's own data takes, so a cargo is
        // typed the way the 360 map spells it rather than invented.
        placeholder={t(`raters.hint.${key}`)}
        autoFocus={key === 'fullName'}
        onChange={(event) => setForm({ ...form, [key]: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && complete) void submit();
          if (event.key === 'Escape') onClose();
        }}
        className="mt-1 w-full rounded-xl border border-line bg-sand px-3 py-2 text-[14px] outline-none placeholder:text-faint/70 focus:border-teal"
      />
    </label>
  );

  return (
    <section className="mt-5 rounded-2xl border border-line bg-paper px-5 py-4 shadow-card">
      <div className="flex flex-wrap items-end gap-3">
        {field('fullName')}
        {field('email', 'email')}
        {field('jobTitle')}
        {field('area')}
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            disabled={busy || !complete}
            onClick={() => void submit()}
            className="cursor-pointer rounded-xl bg-teal px-4 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:opacity-40"
          >
            {busy ? t('common.saving') : t('raters.create')}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer border-none bg-transparent px-1 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>

      <p className="mt-2.5 text-[12.5px] text-faint">{t('raters.createHelp')}</p>
      {error && <p className="mt-2 text-[12.5px] text-[var(--color-critical)]">{error}</p>}
    </section>
  );
};

const InvitationCell = ({ rater }: { rater: RaterListItemDto }) => {
  const { t } = useI18n();

  if (rater.emailIsPlaceholder) {
    return (
      <span className="font-mono text-[10.5px] tracking-[0.06em] text-[#8A6A1F] uppercase">
        {t('raters.noEmail')}
      </span>
    );
  }
  if (rater.timesSent === 0) {
    return (
      <span className="font-mono text-[10.5px] tracking-[0.06em] text-faint uppercase">
        {t('raters.notInvited')}
      </span>
    );
  }
  return (
    <span className="font-mono text-[10.5px] tracking-[0.06em] text-teal-deep uppercase">
      {t('raters.invited', { times: rater.timesSent })}
    </span>
  );
};

export const RatersPage = () => {
  const { t } = useI18n();
  const canInvite = useCan('rater:invite');

  const [data, setData] = useState<RaterListDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  /** Adding somebody to the cycle is editing its map: admin only. */
  const canEditMap = useCan('rater-map:edit');
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    fetchRaters()
      .then(setData)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(load, [load]);

  // Filtered here rather than by refetching: ninety-two rows arrive in one
  // response, and a request per keystroke would be slower and worse.
  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!data) return [];
    if (!needle) return data.raters;
    return data.raters.filter((rater) =>
      [rater.fullName, rater.email, rater.jobTitle ?? '', rater.area ?? '']
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [data, search]);

  if (error) return <Centered>{error}</Centered>;
  if (!data) return <Centered>{t('common.loading')}</Centered>;

  const pending = data.raters.filter(
    (rater) => !rater.completed && rater.timesSent === 0,
  ).length;

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">
            {t('raters.title')}
          </h1>
          <p className="mt-0.5 text-[13.5px] text-soft">
            {t('raters.subtitle', { total: data.total })}
            {pending > 0 && ` · ${t('raters.pendingInvitations', { count: pending })}`}
          </p>
        </div>

        <span className="flex items-center gap-2.5">
          <input
            type="search"
            value={search}
            placeholder={t('raters.searchPlaceholder')}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full max-w-[280px] rounded-xl border border-line bg-paper px-3.5 py-2.5 text-[14px] outline-none placeholder:text-faint focus:border-teal"
          />
          {canEditMap && (
            <button
              type="button"
              onClick={() => setAdding((was) => !was)}
              className="shrink-0 cursor-pointer rounded-xl bg-teal px-3.5 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep"
            >
              {t('raters.new')}
            </button>
          )}
        </span>
      </header>

      {canEditMap && (
        <NewRater
          open={adding}
          onClose={() => setAdding(false)}
          onCreated={(list) => {
            setAdding(false);
            setData(list);
          }}
        />
      )}

      {/* Reloads the list when the run ends, so the invitation column stops
          contradicting the panel above it. */}
      <InvitationRun onFinished={load} />

      <div className="mt-5 overflow-hidden rounded-2xl border border-line bg-paper shadow-card">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-b border-line">
              {[
                t('raters.colPerson'),
                t('raters.colEmail'),
                t('raters.colAssigned'),
                t('raters.colProgress'),
                t('raters.colInvitation'),
              ].map((label) => (
                <th
                  key={label}
                  className="px-4 py-2.5 font-mono text-[9.5px] tracking-[0.12em] text-faint uppercase"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((rater) => (
              <tr
                key={rater.personId}
                className="relative border-b border-line-soft transition last:border-b-0 hover:bg-sand"
              >
                <td className="px-4 py-3">
                  {/* One real link, stretched over the row. The whole row is a
                      hit target without inventing a clickable <tr>, and the
                      accessible name stays the person's — which a row-level
                      onClick would have thrown away. Same pattern as the
                      evaluated list. */}
                  <Link
                    to={`/evaluadores/${rater.personId}`}
                    className="block text-[14.5px] font-semibold after:absolute after:inset-0 after:content-[''] hover:text-teal"
                  >
                    {rater.fullName}
                  </Link>
                  <span className="block text-[12.5px] text-faint">
                    {rater.jobTitle ?? '—'}
                    {rater.area && ` · ${rater.area}`}
                  </span>
                </td>
                <td className="px-4 py-3 text-[13.5px] break-all">
                  {rater.emailIsPlaceholder ? (
                    <span className="text-faint italic">{t('raters.noEmailShort')}</span>
                  ) : (
                    rater.email
                  )}
                </td>
                {/* How many people they were handed, plain. It is the one
                    number on this row that never moves, and reading it off a
                    progress bar meant doing arithmetic to answer "how much was
                    this person asked for". Same phrase as their own card, so
                    the two screens are visibly about the same thing. */}
                <td className="px-4 py-3 font-mono text-[13px] tabular-nums text-soft">
                  {rater.assignedCount}
                </td>
                <td className="px-4 py-3">
                  <Progress share={rater.progressRate} />
                </td>
                <td className="px-4 py-3">
                  <InvitationCell rater={rater} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {shown.length === 0 && (
          <p className="px-4 py-6 text-center text-[13.5px] text-faint">
            {t('raters.noMatches')}
          </p>
        )}
      </div>

      <p className="mt-4 text-xs text-faint">
        {canInvite ? t('raters.footerCanInvite') : t('raters.footerReadOnly')}
      </p>
    </div>
  );
};
